import './action-card.scss';

import { PropsWithChildren, ReactNode } from 'react';

import { Skeleton } from '../../atoms/skeleton/skeleton';
import { Prominence } from '../../atoms/text/text';
import { cn } from '../../utils/helpers';

export type ActionCardProps = PropsWithChildren & {
  className?: string;
  color?: 'negative' | 'warning';
};

/**
 * Action Card
 */
export const ActionCard: React.FC<ActionCardProps> = ({
  color,
  className,
  children,
}) => {
  const colorClass = color
    ? {
        warning: 'ActionCard--warning',
        negative: 'ActionCard--negative',
      }[color]
    : '';

  return (
    <div className={cn(['ActionCard rounded-md', colorClass, className])}>
      {children}
    </div>
  );
};

export const ActionCardSection: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="ActionCard__section py-comfy-sm md:py-comfy-lg pb-comfy-xs px-comfy-sm md:pb-comfy-md md:px-comfy-lg">
    {children}
  </div>
);

/**
 * Action Card Header
 */
export const ActionCardHeader: React.FC<{
  title?: string;
  className?: string;
  badge?: ReactNode;
}> = ({ title, className = '', badge }) => (
  <div
    className={cn(
      ['ActionCard__header flex justify-between items-center', className],
      {
        'mb-comfy-sm md:mb-comfy-md': !badge,
        'mb-comfy-xs md:mb-comfy-sm': !!badge,
      },
    )}
  >
    {title && (
      <h3 className="label-potent md:label-potent label-md md:label-lg">
        {title}
      </h3>
    )}
    {badge}
  </div>
);

/**
 * Action Card Body - if you need full control
 */
export const ActionCardBody: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="ActionCard__body grid gap-snug-xl md:gap-comfy-sm items-center">
    {children}
  </div>
);

export const ActionCardSummary: React.FC<{
  icon?: ReactNode;
  title?: string;
  subTitle?: string;
  prominence?: Prominence;
  isWarning?: boolean;
  isError?: boolean;
}> = ({
  icon,
  title,
  subTitle,
  prominence = 'potent',
  isWarning = false,
  isError = false,
}) => {
  if (!title && !subTitle) {
    return;
  }

  return (
    <>
      {icon}
      {(title || subTitle) && (
        <div className="ActionCard__titlegroup">
          {title && (
            <div
              className={`ActionCard__title label-${prominence} label-md md:label-lg  md:label-${prominence} mb-snug-md`}
            >
              {title}
            </div>
          )}

          {subTitle && (
            <div
              className={cn(
                `label-${prominence} label-sm label-neutral-mild md:label-md`,
                {
                  'label-warning-moderate': isWarning,
                  'label-negative-moderate': isError,
                },
              )}
            >
              {subTitle}
            </div>
          )}
        </div>
      )}
    </>
  );
};

/**
 * Action Card Meta
 *
 * NOTE Organizes right-aligned text with an optional badge.
 */
export const ActionCardMeta: React.FC<{
  text?: string;
  textContrast?: string;
  badge?: ReactNode;
}> = ({ text, textContrast, badge }) => (
  <div className="flex flex-col gap-snug-md md:gap-snug-xl items-end text-right">
    <span className="label-md md:label-lg">
      <span className="label-neutral-amplified">{text}</span>
      {textContrast && (
        <span className="label-neutral-mild">{textContrast}</span>
      )}
    </span>
    {badge}
  </div>
);

export const ActionCardDivider = () => <div className="ActionCard__divider" />;

/**
 * Action Card Footer
 *
 * NOTE that this should almost always take in one or more client components.
 */
export const ActionCardFooter: React.FC<
  PropsWithChildren<{ justify?: 'end' | 'between' }>
> = ({ children, justify = 'between' }) => (
  <ActionCardSection>
    <div
      className={cn([
        'ActionCard__footer flex',
        {
          'justify-between': justify === 'between',
          'justify-end': justify === 'end',
        },
      ])}
    >
      {children}
    </div>
  </ActionCardSection>
);

export const ActionCardSkeleton = () => <Skeleton height={180} />;
